import { useAuth0 } from "@auth0/auth0-react";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/solid";
import { formatPlayerTime } from "@utils/helpers";
import { formatDistanceToNow } from "date-fns";
import { useState } from "react";

import { Dialog, Paragraph, Small } from "@fronterahealth/frontera-ui-components";

import { useDeleteMediaCommentMutation } from "@api/graphql/types-and-hooks";
import { UserInitialsView } from "@components/UserInitial/UserInitialsView";
import { convertReadableString } from "@components/forms/utils";
import CommentEditForm from "@pages/VideoSessionsV2/components/Comments/CommentEditForm";
import { Comment, getEmojiByValue } from "@pages/VideoSessionsV2/components/Comments/helper";
import { useVideoSessionData } from "@providers/VideoSessionProviderV2";

interface CommentCardProps extends Comment {
  onTimeClick: (time: number) => void;
  handleSubmit?: () => void;
  onClick?: () => void;
  hoveredThreadId: string | null;
  handleDelete?: (parentCommentID: string) => void;
}

export const CommentCard: React.FC<CommentCardProps> = ({
  id,
  authorFirstName,
  authorLastName,
  content,
  type,
  time,
  parentCommentID,
  createdDate,
  onTimeClick,
  auth0Id,
  onClick,
  hoveredThreadId,
  handleDelete,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const { user } = useAuth0();
  const { editingCommentId, setEditingCommentId, setAllFilteredComments } = useVideoSessionData();
  const deleteCommentMutation = useDeleteMediaCommentMutation();

  const handleDeleteComment = async () => {
    await deleteCommentMutation
      .mutateAsync({
        commentId: id,
      })
      .then((data) => {
        if (data.removeMediaComment?.status) {
          setIsDialogOpen(false);
          setAllFilteredComments((prevComments) => {
            return !parentCommentID
              ? prevComments.filter((comment) => comment.id !== id)
              : prevComments.map((comment) => {
                  return {
                    ...comment,
                    lastReply: comment.lastReply ? comment.lastReply.filter((reply) => reply.id !== id) : [],
                    totalReplies: comment.totalReplies - 1,
                  };
                });
          });
          if (handleDelete) handleDelete(parentCommentID ?? "");
        }
      });
  };

  return (
    <div key={id} className="relative flex gap-2 w-full transition-colors" onClick={onClick}>
      <UserInitialsView firstName={authorFirstName} lastName={authorLastName} className="min-w-6 max-w-6" />
      {editingCommentId === id ? (
        <CommentEditForm commentId={id} existingContent={content} />
      ) : (
        <>
          <div className="relative items-start gap-2">
            {/* Author Name and Time */}
            <div className="flex flex-col">
              <div className="flex flex-row gap-2 items-start">
                <Small displayType="loud" colorType="primary">
                  {authorFirstName} {authorLastName}
                </Small>
                {!parentCommentID && (
                  <div className="flex gap-3">
                    {/* Time */}
                    <Small
                      colorType="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        onTimeClick(time);
                      }}
                      className="cursor-pointer flex flex-row gap-2 items-start text-interaction-primary"
                    >
                      <p className="font-bold text-text-primary">•</p> {formatPlayerTime(time)}
                    </Small>

                    {/* Comment Type Badge */}
                    <div className="flex flex-raw gap-1">
                      <Small>{type ? getEmojiByValue(type) : null}</Small>
                      <Small className="text-black">{convertReadableString(type)}</Small>
                    </div>
                  </div>
                )}
              </div>

              <Small displayType="normal">{formatDistanceToNow(new Date(createdDate), { addSuffix: true })}</Small>
            </div>

            {/* Comment Text */}
            <Paragraph className="text-[#454545] whitespace-pre-wrap wrap-anywhere">{content}</Paragraph>
          </div>

          {/* Action buttons */}
          {hoveredThreadId &&
          (hoveredThreadId === parentCommentID || hoveredThreadId === id) &&
          user?.sub === auth0Id ? (
            <div className="absolute right-2 flex items-center space-x-3">
              <TrashIcon
                className={`w-4 h-4 cursor-pointer text-text-secondary hover:text-error-600 ${deleteCommentMutation.isPending ? "pointer-events-none" : "pointer-events-auto"}`}
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!parentCommentID) {
                    setIsDialogOpen(true);
                  } else {
                    await handleDeleteComment();
                  }
                }}
              />
              <PencilIcon
                className="w-4 h-4 cursor-pointer text-text-secondary hover:text-primary-600"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingCommentId(id);
                }}
              />
            </div>
          ) : null}
        </>
      )}

      {/* Delete Dialog */}
      <Dialog
        size="sm"
        showCloseButton={false}
        title="Delete Comment"
        open={isDialogOpen}
        setOpen={setIsDialogOpen}
        primaryButton={{
          text: "Yes, Delete",
          onClick: handleDeleteComment,
          type: "submit",
          buttonAction: "destructive",
          disabled: deleteCommentMutation.isPending,
        }}
        secondaryButton={{
          text: "No, Cancel",
          onClick: () => setIsDialogOpen(false),
        }}
      >
        <div className="py-2 text-text-primary text-base">
          Are you sure you want to delete this comment? This will remove the entire conversation.
        </div>
      </Dialog>
    </div>
  );
};
